import UserFriendlyError from '@/errors/UserFriendlyError';
import FatalServerError from '@/errors/FatalServerError';

// Known third-party signatures: browser extensions, masked URLs, and specific vendored/CDN
// scripts that are not part of our built bundle. A frame must match one of these to be
// considered third-party — this avoids false positives in local/dev/test stacks, which never
// contain a real production bundle path but are still first-party code.
const THIRD_PARTY_PATTERNS = [
  /chrome-extension:\/\//,
  /moz-extension:\/\//,
  /webkit-masked-url:\/\//,
  /goguardian/i,
  /tex-mml-chtml\.js/, // MathJax CDN bundle
  /\/libs\/calculator\.js/, // Desmos calculator (vendored, not part of our app bundle)
];

/**
 * Returns true if the error originates entirely from third-party code.
 * A stack frame referencing our built bundle (/assets/) is definitive proof of first-party
 * origin. Otherwise, the error is only treated as third-party when a frame matches a known
 * third-party signature (extension, masked URL, or a recognized external/vendored script).
 */
function isThirdPartyError(error: unknown): boolean {
  if (!(error instanceof Error) || !error.stack) return false;

  const frames = error.stack.split('\n').filter((line) => line.trim().startsWith('at ') || line.includes('@'));
  if (frames.length === 0) return false;

  if (frames.some((f) => f.includes('/assets/'))) return false;

  return frames.some((f) => THIRD_PARTY_PATTERNS.some((p) => p.test(f)));
}

function isMathJaxRetry(error: unknown): boolean {
  return error instanceof Error && error.message === 'MathJax retry';
}

/**
 * MathJax DOM errors when typeset() fires after Vue destroys the component.
 * Only needed when our /assets/ code IS in the stack (e.g. nextTick → typeset).
 * When our code is NOT in the stack, isThirdPartyError catches it instead.
 *
 * Note: DOMException does not extend Error in Node/V8 (spec-compliant), but it does in
 * most browsers. We accept both so this check behaves the same in tests and in production.
 */
function isMathJaxDomError(error: unknown): boolean {
  if (!(error instanceof Error) && !(error instanceof DOMException)) return false;
  if (!(error as Error | DOMException).stack?.includes('tex-mml-chtml')) return false;

  // TypeError: Cannot read properties of null (reading 'replaceChild') etc.
  if (error instanceof TypeError) {
    return /reading '(replaceChild|insertBefore|removeChild|parentNode|nodeType)'/.test(error.message);
  }

  // DOMException: Failed to execute 'splitText' on 'Text' (stale offset after DOM mutation)
  if (error instanceof DOMException) {
    return /splitText/.test(error.message);
  }

  return false;
}

function isResizeObserverLoopError(error: unknown): boolean {
  if (typeof error === 'string') return error.includes('ResizeObserver loop');
  if (error instanceof Error) return error.message.includes('ResizeObserver loop');
  return false;
}

const DYNAMIC_IMPORT_PATTERN = /Failed to fetch dynamically imported module|Unable to preload CSS/;

function isDynamicImportError(error: unknown): boolean {
  if (typeof error === 'string') return DYNAMIC_IMPORT_PATTERN.test(error);
  if (error instanceof Error) return DYNAMIC_IMPORT_PATTERN.test(error.message);
  return false;
}

const ROLLBAR_NO_REASON = '[unhandledrejection] error getting `reason` from event';

function isUnhandledRejectionWithoutReason(error: unknown): boolean {
  if (typeof error === 'string') return error === ROLLBAR_NO_REASON;
  if (error instanceof Error) return error.message.includes(ROLLBAR_NO_REASON);
  return false;
}

function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (error as any).code === 'ERR_NETWORK';
}

/**
 * Cross-origin "Script error." — the browser strips all details (message, file, line, stack)
 * from errors thrown by scripts loaded from a different origin (extensions, injected ads, CDN).
 * isThirdPartyError() can't catch these because there are no stack frames to match against.
 */
function isCrossOriginScriptError(error: unknown): boolean {
  if (typeof error === 'string') return error === 'Script error.' || error === 'Script error';
  if (error instanceof Error) return error.message === 'Script error.' || error.message === 'Script error';
  return false;
}

/**
 * Fullscreen API rejections — requestFullscreen() fails when called without a user gesture
 * (transient activation) or when a pending fullscreen operation is interrupted by another call.
 * The fullscreen either works or silently fails from the user's perspective — not actionable.
 */
const FULLSCREEN_ERROR_PATTERN = /Cannot request fullscreen|Pending operation cancelled by requestFullscreen/;
function isFullscreenApiError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return FULLSCREEN_ERROR_PATTERN.test(error.message);
}

// ChromeOS enterprise policy or management extension (GoGuardian etc.) — no app stack frames
function isBrowserPermissionsError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.message === 'Permissions check failed';
}

function isUserFriendlyError(error: unknown): boolean {
  return error instanceof UserFriendlyError || (error as any)?.name === 'UserFriendlyError';
}

function isFatalServerError(error: unknown): boolean {
  return error instanceof FatalServerError || (error as any)?.name === 'FatalServerError';
}

export function shouldIgnoreRollbarError(args: unknown[]): boolean {
  const error0 = args[0];
  const error1 = args[1];

  if (isUserFriendlyError(error0) || isUserFriendlyError(error1)) return true;

  if (
    error1 &&
    typeof error1 === 'object' &&
    ((error1 as any).name === 'AuthError' || (error1 as any).status === 401 || (error1 as any).status === 403)
  ) {
    return true;
  }

  if (error0 && (error0 as any).name === 'AuthError') return true;

  const navigationPattern = /^(Redirected|Navigation cancelled|Navigation aborted|Avoided redundant navigation)\b/;
  if (error0 instanceof Error && navigationPattern.test(error0.message)) return true;
  if (error1 instanceof Error && navigationPattern.test(error1.message)) return true;

  // Errors originating entirely from third-party code (extensions, CDN libs, injected scripts)
  if (isThirdPartyError(error0) || isThirdPartyError(error1)) return true;

  // MathJax internal control flow — not a real error
  if (isMathJaxRetry(error0) || isMathJaxRetry(error1)) return true;

  // MathJax DOM errors when our code IS in the stack (nextTick → typeset race condition)
  if (isMathJaxDomError(error0) || isMathJaxDomError(error1)) return true;

  if (error1 && typeof error1 === 'object' && 'isTrusted' in (error1 as any)) return true;

  // ResizeObserver loop warnings are benign browser-level notifications, not real errors
  if (isResizeObserverLoopError(error0) || isResizeObserverLoopError(error1)) return true;

  // Dynamic import failures are handled by router.onError which reloads the page
  if (isDynamicImportError(error0) || isDynamicImportError(error1)) return true;

  // Rollbar SDK fallback when a Promise is rejected without a reason (Safari / extensions)
  if (isUnhandledRejectionWithoutReason(error0) || isUnhandledRejectionWithoutReason(error1)) return true;

  // Network errors (ERR_NETWORK) are client-side connectivity issues — nothing actionable server-side
  if (isNetworkError(error0) || isNetworkError(error1)) return true;

  // Cross-origin "Script error." — no stack frames, so isThirdPartyError() can't match it
  if (isCrossOriginScriptError(error0) || isCrossOriginScriptError(error1)) return true;

  // Fullscreen API rejections — benign browser behavior, not actionable
  if (isFullscreenApiError(error0) || isFullscreenApiError(error1)) return true;

  // ChromeOS enterprise policy / management extension — no app stack frames, not actionable
  if (isBrowserPermissionsError(error0) || isBrowserPermissionsError(error1)) return true;

  // 500+ errors are server-side issues tracked by backend Rollbar — no need to double-report from frontend
  if (isFatalServerError(error0) || isFatalServerError(error1)) return true;

  return false;
}
